<template>
  <modal-page
    ref="contactModal"
    :isLoading="isLoading"
    @close="closeModal"
  >
    <template v-slot:header>
      <contact-header v-if="!isLoading"></contact-header>
    </template>

    <template v-slot:single>
      <contact-details></contact-details>
      <contact-notes></contact-notes>
      <contact-records></contact-records>
      <contact-types-tags></contact-types-tags>
      <contact-custom-fields v-if="customFields.length"></contact-custom-fields>
      <contact-type-fields
        v-for="type in contactContactTypes"
        :key="type.id"
        :contact-contact-type="type"
      ></contact-type-fields>
      <contact-timeline :is-loading="lazyProps.communicationLogs.isLoading"></contact-timeline>
    </template>

    <template v-slot:left>
      <contact-details></contact-details>
      <contact-types-tags></contact-types-tags>
      <contact-custom-fields v-if="customFields.length"></contact-custom-fields>
      <contact-type-fields
        v-for="type in contactContactTypes"
        :key="type.id"
        :contact-contact-type="type"
      ></contact-type-fields>
    </template>

    <template v-slot:middle>
      <contact-notes></contact-notes>
      <contact-timeline :is-loading="lazyProps.communicationLogs.isLoading"></contact-timeline>
    </template>

    <template v-slot:right>
      <contact-records></contact-records>
      <contact-follow-up></contact-follow-up>
    </template>

    <template v-slot:footer>
      <button-link
        class="mr-auto"
        color="is-danger"
        @click="deleteContact"
        :loading="deleting"
      >Delete Contact</button-link>
    </template>
  </modal-page>
</template>

<script>
import {subscribeToChannel, unsubscribeToChannel} from "../../services/pusher";
import ContactHeader from "./sections/ContactHeader";
import ContactDetails from "./sections/ContactDetails";
import ContactTimeline from "./sections/ContactTimeline";
import ContactRecords from "./sections/ContactRecords";
import ContactTypesTags from "./sections/ContactTypesTags";
import ContactTypeFields from "./sections/ContactTypeFields";
import {EventBus} from "../../event-bus";
import ContactCustomFields from "./sections/ContactCustomFields";
import ContactNotes from "./sections/ContactNotes"
import ContactFollowUp from "./sections/ContactFollowUp.vue";

export default {
  name: "ContactModal",
  components: {
    ContactFollowUp,
    ContactNotes,
    ContactCustomFields,
    ContactTypesTags,
    ContactTypeFields,
    ContactRecords,
    ContactTimeline,
    ContactDetails,
    ContactHeader,
  },
  data() {
    return {
      isLoading: false,
      deleting: false,
      contactId: undefined,
      requiredProps: [
        'contact',
        'tags',
        'contactContactTypes',
        'followUpSequences',
      ],
      eagerProps: {
        records: {isLoading: true},
        notes: {isLoading: true},
      },
      lazyProps: {
        communicationLogs: {isLoading: true},
        // followUpSequences: {isLoading: true},
      }
    }
  },
  watch: {
    '$route' () {
      this.openModal();
    },
  },
  mounted() {
    this.openModal();
    EventBus.$on('closeContactModal', () => {
      this.$refs.contactModal.close();
    });
  },
  destroyed() {
    EventBus.$off('closeContactModal');
  },
  computed: {
    contactContactTypes() {
      if(this.$store.state.activeContact) {
        return this.$store.state.activeContact.contactContactTypes || [];
      }

      return [];
    },
    customFields() {
      return this.$store.getters.contactCustomFields;
    }
  },
  methods: {
    getContact() {
      this.isLoading = true;

      subscribeToChannel('activeContact', this.contactId);

      // Required
      let requiredData = this.$api.clientStore.initContact(this.contactId, this.requiredProps)

      // Immediately Start
      const eagerData = Object.keys(this.eagerProps).map((prop) => {
        return {
          prop: prop,
          promise: this.$api.clientStore.getContactProp(this.contactId, prop),
        };
      });

      // Soon as Required is Done
      requiredData.then((response) => {
        // Sets the Required Initial Data
        this.$store.commit('setActiveContact', response.data);
        this.isLoading = false;

        // Once all the other More Important Requests have finished loading in the data
        eagerData.forEach((dataSet) => {
          this.eagerProps[dataSet.prop].isLoading = true
          dataSet.promise.then((response) => {
            // Need to make sure the activeContact still set
            // It wont be if the user closes the modal before these values are returned
            if(this.$store.state.activeContact) {
              this.$store.commit('setActiveContactProp', [dataSet.prop, response.data]);
              this.eagerProps[dataSet.prop].isLoading = false;
            }
          })
        });

        // Start Loading in the last set of data
        Object.keys(this.lazyProps).forEach((prop) => {
          this.lazyProps[prop].isLoading = true
          this.$api.clientStore.getContactProp(this.contactId, prop)
          .then((response) => {
            // Need to make sure the activeContact still set
            if(this.$store.state.activeContact) {
              this.$store.commit('setActiveContactProp', [prop, response.data]);
              this.lazyProps[prop].isLoading = false;
            }
          });
        });
      });
    },
    openModal() {
      this.contactId = this.$route.query.contact;

      if (this.contactId) {
        this.getContact();
        this.$refs.contactModal.open();
      }
    },
    closeModal() {
      this.$router.replace({
        ...this.$router.currentRoute,
        query: {contact: undefined}
      });

      unsubscribeToChannel('activeContact', this.contactId);
      this.$store.commit('setActiveContact', null);
    },
    deleteContact() {
      this.$utils.confirm(`Are you sure you want to <strong>delete</strong> this contact? All associated data will be deleted and will not be recoverable.`, 'Delete Contact')
        .then(() => {
          this.deleting = true
          this.$api.contacts.delete(this.contactId)
            .then(() => {
              this.$buefy.toast.open({
                message: 'Contact was deleted',
                type: 'is-success'
              });

              this.$store.commit('events/toggleEvent', 'reloadContactTable')
              this.$refs.contactModal.close()
            })
            .finally(() => this.deleting = false)
        });

    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

</style>
